import React, { PureComponent, type ReactNode, useMemo } from 'react';

import { useIntl } from 'react-intl';

import AkFlag, { type FlagProps } from '@atlaskit/flag';
import SuccessIcon from '@atlaskit/icon/core/status-success';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { type Invitees } from '../../common/types';
import { withLocalIntl } from '../../common/utils/i18n/intl-provider';
import { getProductDisplayName, type Product } from '../../common/utils/products';
import { clampString } from '../../common/utils/string';

import { messages } from './messages';

export type Props = {
	id: FlagProps['id'];
	invitees: Invitees;
	onDismiss: FlagProps['onDismissed'];
	product: Product;
};

const MAX_LENGTH = 25;

export const InviteSuccessFlag: (props: Props) => React.JSX.Element = withLocalIntl(
	({ invitees, onDismiss, product, ...props }: Props) => {
		const { formatMessage } = useIntl();

		const inviteeEmailOrName = clampString(invitees[0].email ?? invitees[0].name, MAX_LENGTH);
		const productDisplayName = getProductDisplayName(product);

		const customFlagFooterProps = useMemo(
			(): Pick<FlagProps, 'actions' | 'linkComponent'> => ({
				actions: [
					{
						content: '',
						onClick: () => {},
					},
				],
				linkComponent: class extends PureComponent {
					render() {
						return (
							<Box as="p" xcss={footerStyles}>
								{formatMessage(messages.additionalInfoGeneric, {
									productDisplayName,
								})}
							</Box>
						);
					}
				},
			}),
			[formatMessage, productDisplayName],
		);

		return (
			<AkFlag
				{...props}
				{...customFlagFooterProps}
				icon={
					<SuccessIcon spacing="spacious" label="Success" color={token('color.icon.success')} />
				}
				onDismissed={onDismiss}
				title={
					<Box as="p" xcss={subtleColorStyles}>
						{formatMessage(messages.title, {
							inviteeCount: invitees.length,
						})}
					</Box>
				}
				description={
					<Text as="p">
						{invitees.length === 1
							? formatMessage(messages.descriptionGenericOne, {
									strong: (...chunks: ReactNode[]) => <Text as="strong">{chunks.join('')}</Text>,
									inviteeEmailOrName: inviteeEmailOrName,
									productDisplayName,
								})
							: formatMessage(messages.descriptionGenericPlural, {
									strong: (...chunks: ReactNode[]) => <Text as="strong">{chunks.join('')}</Text>,
									inviteeCount: invitees.length,
									productDisplayName,
								})}
					</Text>
				}
			/>
		);
	},
);

const footerStyles = xcss({
	color: 'color.text.subtle',
	transform: 'translateX(2px)',
});

const subtleColorStyles = xcss({
	color: 'color.text.subtle',
});
